import { RapidStateCreator } from "../types";
import type { SourceDocument } from "@rapid/hooks/useImproveBatchLoader";
import type { MediaItem } from "@rapid/types/media";
import type { AiToolUsed } from "@rapid/utils/api/datasets";

/**
 * Improve batch data stored in Zustand
 */
export interface ImproveBatchState {
    sources: SourceDocument[];
    batchRef: string;
    datasetId: string;
    annotationGroup: string;
}

/**
 * State slice for the Improve flow
 * Centralizes all improve-related state that was previously split between
 * ImproveProvider and useImproveBatchLoader
 */
export interface ImproveSliceState {
    // Improve batch state
    improveBatch: ImproveBatchState | null;
    improveLoading: boolean;
    improveError: Error | null;

    // Limited/shuffled sources for the current session (max 10)
    limitedSources: SourceDocument[];

    // Track which sources have been loaded (to avoid reloading)
    loadedSourceIds: Set<string>;

    // Track how many images have been approved in this session
    approvedCount: number;

    // Track which images have been approved (by source ID)
    approvedImageIds: Set<string>;

    // Store JSON snapshots of annotations at time of approval (for detecting changes)
    approvedAnnotationSnapshots: Map<string, string>;

    // Media items created from sources (for display in carousel)
    improveMediaItems: MediaItem[];

    // Image loading state (downloading images from CDN)
    isLoadingImages: boolean;

    // Whether initial load has completed (to prevent re-initialization)
    hasInitialized: boolean;

    // Pending AI tool for annotation sync (set before inference, consumed on sync)
    pendingAiTool: AiToolUsed;

    // Getters
    getImproveBatch: () => ImproveBatchState | null;
    getImproveDatasetId: () => string | null;
    getImproveAnnotationGroup: () => string | null;
    getImproveSources: () => SourceDocument[];
    getLimitedSources: () => SourceDocument[];
    getImproveMediaItems: () => MediaItem[];
    getImproveLoading: () => boolean;
    getPendingAiTool: () => AiToolUsed;
    getApprovedCount: () => number;
    getApprovedImageIds: () => Set<string>;
    isImageApproved: (imageId: string) => boolean;
    getApprovedAnnotationSnapshot: (imageId: string) => string | undefined;
    getTotalBatchCount: () => number;
    /** Get current round number (1-indexed) */
    getCurrentRound: () => number;
    /** Get total number of rounds */
    getTotalRounds: () => number;
    /** Get approved count within current round (for stepped progress display) */
    getApprovedInCurrentRound: () => number;
    /** Get total images in current round (may be less than 20 for final round) */
    getTotalInCurrentRound: () => number;

    // Setters
    /** Set batch data. If prioritySourceId is provided, ensure that source is included in limitedSources */
    setImproveBatch: (batch: ImproveBatchState | null, prioritySourceId?: string) => void;
    setImproveLoading: (loading: boolean) => void;
    setImproveError: (error: Error | null) => void;
    setLimitedSources: (sources: SourceDocument[]) => void;
    appendLimitedSource: (source: SourceDocument) => void;
    setImproveMediaItems: (items: MediaItem[]) => void;
    setIsLoadingImages: (loading: boolean) => void;
    setHasInitialized: (initialized: boolean) => void;
    /** Set pending AI tool to be used on next annotation sync, then cleared */
    setPendingAiTool: (tool: AiToolUsed) => void;
    /** Clear pending AI tool (called after sync consumes it) */
    clearPendingAiTool: () => void;

    // Actions
    clearImproveState: () => void;
    /** Remove a source from limitedSources by ID (optimistic removal on approve) */
    removeSourceById: (sourceId: string) => void;
    /** Load more sources when running low - returns IDs of newly added sources */
    loadMoreSources: () => string[];
    /** Increment approved count */
    incrementApprovedCount: () => void;
    /** Mark an image as approved (adds to approvedImageIds set and stores annotation snapshot) */
    markImageAsApproved: (imageId: string, annotationSnapshot: string) => void;
}

const MAX_IMAGES_PER_BATCH = 10;

/**
 * Fisher-Yates shuffle algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export const createImproveSlice: RapidStateCreator<ImproveSliceState> = (set, get) => ({
    // Initial state
    improveBatch: null,
    improveLoading: false,
    improveError: null,
    limitedSources: [],
    loadedSourceIds: new Set<string>(),
    approvedCount: 0,
    approvedImageIds: new Set<string>(),
    approvedAnnotationSnapshots: new Map<string, string>(),
    improveMediaItems: [],
    isLoadingImages: false,
    hasInitialized: false,
    pendingAiTool: false,

    // Getters
    getImproveBatch: () => get().improveBatch,
    getImproveDatasetId: () => get().improveBatch?.datasetId ?? null,
    getImproveAnnotationGroup: () => get().improveBatch?.annotationGroup ?? null,
    getImproveSources: () => get().improveBatch?.sources ?? [],
    getLimitedSources: () => get().limitedSources,
    getImproveMediaItems: () => get().improveMediaItems,
    getImproveLoading: () => get().improveLoading || get().isLoadingImages,
    getPendingAiTool: () => get().pendingAiTool,
    getApprovedCount: () => get().approvedCount,
    getApprovedImageIds: () => get().approvedImageIds,
    isImageApproved: (imageId: string) => get().approvedImageIds.has(imageId),
    getApprovedAnnotationSnapshot: (imageId: string) =>
        get().approvedAnnotationSnapshots.get(imageId),
    getTotalBatchCount: () => get().improveBatch?.sources?.length ?? 0,
    getCurrentRound: () => {
        const approved = get().approvedCount;
        return Math.floor(approved / MAX_IMAGES_PER_BATCH) + 1;
    },
    getTotalRounds: () => {
        const total = get().improveBatch?.sources?.length ?? 0;
        return Math.ceil(total / MAX_IMAGES_PER_BATCH);
    },
    getApprovedInCurrentRound: () => {
        const approved = get().approvedCount;
        return approved % MAX_IMAGES_PER_BATCH;
    },
    getTotalInCurrentRound: () => {
        const total = get().improveBatch?.sources?.length ?? 0;
        const currentRound = Math.floor(get().approvedCount / MAX_IMAGES_PER_BATCH) + 1;
        const totalRounds = Math.ceil(total / MAX_IMAGES_PER_BATCH);
        // Last round may have fewer images
        if (currentRound === totalRounds) {
            const remainder = total % MAX_IMAGES_PER_BATCH;
            return remainder === 0 ? MAX_IMAGES_PER_BATCH : remainder;
        }
        return MAX_IMAGES_PER_BATCH;
    },

    // Setters
    setImproveBatch: (batch, prioritySourceId) => {
        if (batch) {
            // When setting batch, also shuffle and limit sources
            const shuffled = shuffleArray(batch.sources);
            let limited = shuffled.slice(0, MAX_IMAGES_PER_BATCH);

            // If a prioritySourceId is provided, ensure it's in the limited sources
            if (prioritySourceId) {
                const priorityInLimited = limited.some((s) => s.id === prioritySourceId);
                if (!priorityInLimited) {
                    // Find the priority source in all sources
                    const prioritySource = batch.sources.find((s) => s.id === prioritySourceId);
                    if (prioritySource) {
                        // Replace the first item with the priority source
                        limited = [prioritySource, ...limited.slice(0, MAX_IMAGES_PER_BATCH - 1)];
                        console.log(
                            "[improveSlice] setImproveBatch - added priority source to limited:",
                            prioritySourceId
                        );
                    }
                }
            }

            // Track which sources have been loaded
            const loadedIds = new Set(limited.map((s) => s.id));

            console.log(
                "[improveSlice] setImproveBatch - shuffled and limited sources:",
                batch.sources.length,
                "->",
                limited.length
            );
            set({
                improveBatch: batch,
                limitedSources: limited,
                loadedSourceIds: loadedIds,
                approvedCount: 0,
                approvedImageIds: new Set<string>(),
                approvedAnnotationSnapshots: new Map<string, string>()
            });
        } else {
            set({
                improveBatch: null,
                limitedSources: [],
                loadedSourceIds: new Set(),
                approvedCount: 0,
                approvedImageIds: new Set<string>(),
                approvedAnnotationSnapshots: new Map<string, string>()
            });
        }
    },
    setImproveLoading: (loading) => set({ improveLoading: loading }),
    setImproveError: (error) => set({ improveError: error }),
    setLimitedSources: (sources) => set({ limitedSources: sources }),
    appendLimitedSource: (source) =>
        set((state) => ({ limitedSources: [...state.limitedSources, source] })),
    setImproveMediaItems: (items) => set({ improveMediaItems: items }),
    setIsLoadingImages: (loading) => set({ isLoadingImages: loading }),
    setHasInitialized: (initialized) => set({ hasInitialized: initialized }),
    setPendingAiTool: (tool) => set({ pendingAiTool: tool }),
    clearPendingAiTool: () => set({ pendingAiTool: false }),

    // Actions
    clearImproveState: () =>
        set({
            improveBatch: null,
            improveLoading: false,
            improveError: null,
            limitedSources: [],
            loadedSourceIds: new Set(),
            approvedCount: 0,
            approvedImageIds: new Set<string>(),
            approvedAnnotationSnapshots: new Map<string, string>(),
            improveMediaItems: [],
            isLoadingImages: false,
            hasInitialized: false,
            pendingAiTool: false
        }),
    removeSourceById: (sourceId: string) => {
        set((state) => {
            const newLimitedSources = state.limitedSources.filter((s) => s.id !== sourceId);
            console.log(
                "[improveSlice] removeSourceById:",
                sourceId,
                "remaining:",
                newLimitedSources.length
            );
            return { limitedSources: newLimitedSources };
        });
    },
    loadMoreSources: () => {
        const state = get();
        const allSources = state.improveBatch?.sources ?? [];
        const loadedIds = state.loadedSourceIds;

        // Find sources that haven't been loaded yet
        const unloadedSources = allSources.filter((s) => !loadedIds.has(s.id));

        if (unloadedSources.length === 0) {
            console.log("[improveSlice] loadMoreSources - no more sources to load");
            return [];
        }

        // Take next batch of sources
        const newSources = unloadedSources.slice(0, MAX_IMAGES_PER_BATCH);
        const newIds = newSources.map((s) => s.id);

        // Update loaded IDs
        const updatedLoadedIds = new Set(loadedIds);
        newIds.forEach((id) => updatedLoadedIds.add(id));

        console.log(
            "[improveSlice] loadMoreSources - loading",
            newSources.length,
            "more sources. Total loaded:",
            updatedLoadedIds.size,
            "of",
            allSources.length
        );

        set((state) => ({
            limitedSources: [...state.limitedSources, ...newSources],
            loadedSourceIds: updatedLoadedIds
        }));

        return newIds;
    },
    incrementApprovedCount: () => {
        set((state) => {
            const newCount = state.approvedCount + 1;
            console.log("[improveSlice] incrementApprovedCount:", newCount);
            return { approvedCount: newCount };
        });
    },
    markImageAsApproved: (imageId: string, annotationSnapshot: string) => {
        set((state) => {
            const newApprovedIds = new Set(state.approvedImageIds);
            newApprovedIds.add(imageId);
            const newSnapshots = new Map(state.approvedAnnotationSnapshots);
            newSnapshots.set(imageId, annotationSnapshot);
            console.log(
                "[improveSlice] markImageAsApproved:",
                imageId,
                "snapshot length:",
                annotationSnapshot.length
            );
            return { approvedImageIds: newApprovedIds, approvedAnnotationSnapshots: newSnapshots };
        });
    }
});
